import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent, type TouchEvent, type WheelEvent } from 'react';
import { Button, Description, Spinner } from '@heroui/react';
import { Crosshair, RotateCcw, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import {
  DEFAULT_FLOOR_PLAN_RECT_SIZE,
  FLOOR_PLAN_ZOOM_STEP,
  MAX_FLOOR_PLAN_ZOOM,
  MIN_FLOOR_PLAN_ZOOM,
  clamp,
  displayedImageSize,
  normalizeRect,
  rectFromClick,
  scrollOffsetToCenterRect,
  zoomToCenterRect,
  type FloorPlanRect,
} from './floor-plan-utils';

interface FloorPlanMarkerProps {
  imageUrl: string;
  rect: FloorPlanRect | null;
  onChange?: (rect: FloorPlanRect | null) => void;
  autoFocus?: boolean;
  className?: string;
}

interface NaturalSize {
  width: number;
  height: number;
}

interface BoxSize {
  width: number;
  height: number;
}

function pointerToRelative(
  event: { clientX: number; clientY: number },
  image: HTMLElement,
): { x: number; y: number } {
  const bounds = image.getBoundingClientRect();
  const x = bounds.width > 0 ? (event.clientX - bounds.left) / bounds.width : 0;
  const y = bounds.height > 0 ? (event.clientY - bounds.top) / bounds.height : 0;
  return { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
}

function RectOverlay({
  rect,
  dashed = false,
}: {
  rect: FloorPlanRect;
  dashed?: boolean;
}) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute border-2 bg-danger/30 ${
        dashed ? 'border-dashed border-danger' : 'border-danger'
      }`}
      style={{
        left: `${rect.x * 100}%`,
        top: `${rect.y * 100}%`,
        width: `${rect.width * 100}%`,
        height: `${rect.height * 100}%`,
      }}
    />
  );
}

export function FloorPlanMarker({
  imageUrl,
  rect,
  onChange,
  autoFocus = true,
  className = '',
}: FloorPlanMarkerProps) {
  const editable = typeof onChange === 'function';
  const scrollRef = useRef<HTMLDivElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);
  const pendingFocusZoomRef = useRef<number | null>(null);
  const hasAutoFocusedRef = useRef(false);
  const pinchRef = useRef<{ distance: number; zoom: number } | null>(null);
  const drawingRef = useRef<{ x: number; y: number } | null>(null);

  const [naturalSize, setNaturalSize] = useState<NaturalSize | null>(null);
  const [boxSize, setBoxSize] = useState<BoxSize>({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState<FloorPlanRect | null>(null);

  useEffect(() => {
    hasAutoFocusedRef.current = false;
  }, [rect?.x, rect?.y, rect?.width, rect?.height, autoFocus]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setBoxSize({ width, height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [naturalSize]);

  useEffect(() => {
    const el = imageWrapRef.current;
    if (!el) return;
    const preventPinchScroll = (event: globalThis.TouchEvent) => {
      if (event.touches.length >= 2) event.preventDefault();
    };
    el.addEventListener('touchmove', preventPinchScroll, { passive: false });
    return () => el.removeEventListener('touchmove', preventPinchScroll);
  }, [naturalSize]);

  const display =
    naturalSize && boxSize.width > 0 && boxSize.height > 0
      ? displayedImageSize(
          naturalSize.width,
          naturalSize.height,
          boxSize.width,
          boxSize.height,
          zoom,
        )
      : null;

  const applyScrollToRect = useCallback(
    (target: FloorPlanRect, nextZoom: number, behavior: ScrollBehavior) => {
      const el = scrollRef.current;
      if (!el || !naturalSize || boxSize.width <= 0) return;
      const size = displayedImageSize(
        naturalSize.width,
        naturalSize.height,
        boxSize.width,
        boxSize.height,
        nextZoom,
      );
      const offset = scrollOffsetToCenterRect(
        target,
        size.width,
        size.height,
        el.clientWidth,
        el.clientHeight,
      );
      el.scrollTo({ ...offset, behavior });
    },
    [boxSize.height, boxSize.width, naturalSize],
  );

  const focusRect = useCallback(
    (target: FloorPlanRect, behavior: ScrollBehavior = 'smooth') => {
      if (!naturalSize || boxSize.width <= 0) return;
      const nextZoom = zoomToCenterRect();
      pendingFocusZoomRef.current = nextZoom;
      setZoom(nextZoom);
      requestAnimationFrame(() => {
        applyScrollToRect(target, nextZoom, behavior);
      });
    },
    [applyScrollToRect, boxSize.height, boxSize.width, naturalSize],
  );

  useLayoutEffect(() => {
    if (pendingFocusZoomRef.current == null || pendingFocusZoomRef.current !== zoom) return;
    if (!rect || !display) return;
    applyScrollToRect(rect, zoom, 'smooth');
    pendingFocusZoomRef.current = null;
  }, [applyScrollToRect, display, rect, zoom]);

  useEffect(() => {
    if (!autoFocus || !rect || hasAutoFocusedRef.current) return;
    if (!naturalSize || boxSize.width < 8 || boxSize.height < 8) return;
    hasAutoFocusedRef.current = true;
    focusRect(rect, 'auto');
  }, [autoFocus, boxSize.height, boxSize.width, focusRect, naturalSize, rect]);

  const changeZoom = (next: number, around?: { x: number; y: number }) => {
    const el = scrollRef.current;
    const clamped = clamp(next, MIN_FLOOR_PLAN_ZOOM, MAX_FLOOR_PLAN_ZOOM);
    if (!el || !naturalSize || !display || clamped === zoom) {
      setZoom(clamped);
      return;
    }

    const relX = around?.x ?? (el.scrollLeft + el.clientWidth / 2) / display.width;
    const relY = around?.y ?? (el.scrollTop + el.clientHeight / 2) / display.height;
    const nextSize = displayedImageSize(
      naturalSize.width,
      naturalSize.height,
      boxSize.width,
      boxSize.height,
      clamped,
    );
    setZoom(clamped);
    requestAnimationFrame(() => {
      el.scrollTo({
        left: clamp(relX * nextSize.width - el.clientWidth / 2, 0, Math.max(0, nextSize.width - el.clientWidth)),
        top: clamp(relY * nextSize.height - el.clientHeight / 2, 0, Math.max(0, nextSize.height - el.clientHeight)),
      });
    });
  };

  const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    const image = imageWrapRef.current;
    const around = image ? pointerToRelative(event, image) : undefined;
    const direction = event.deltaY < 0 ? 1 : -1;
    changeZoom(zoom + direction * FLOOR_PLAN_ZOOM_STEP, around);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!editable || !onChange) return;
    if (event.pointerType === 'touch' && event.isPrimary === false) return;
    if (pinchRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointerToRelative(event, event.currentTarget);
    drawingRef.current = point;
    setDraft(rectFromClick(point.x, point.y, 0.002));
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = drawingRef.current;
    if (!start) return;
    const point = pointerToRelative(event, event.currentTarget);
    setDraft(normalizeRect(start.x, start.y, point.x, point.y));
  };

  const finishDrawing = (event: PointerEvent<HTMLDivElement>) => {
    const start = drawingRef.current;
    if (!start || !onChange) return;
    const point = pointerToRelative(event, event.currentTarget);
    const dx = Math.abs(point.x - start.x);
    const dy = Math.abs(point.y - start.y);
    const next =
      dx < 0.008 && dy < 0.008
        ? rectFromClick(start.x, start.y, DEFAULT_FLOOR_PLAN_RECT_SIZE)
        : normalizeRect(start.x, start.y, point.x, point.y);
    drawingRef.current = null;
    setDraft(null);
    onChange(next);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      const [a, b] = [event.touches[0], event.touches[1]];
      if (!a || !b) return;
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      pinchRef.current = { distance, zoom };
      drawingRef.current = null;
      setDraft(null);
    }
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 2 || !pinchRef.current) return;
    event.preventDefault();
    const [a, b] = [event.touches[0], event.touches[1]];
    if (!a || !b) return;
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    if (pinchRef.current.distance <= 0) return;
    changeZoom(pinchRef.current.zoom * (distance / pinchRef.current.distance));
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (event.touches.length < 2) {
      pinchRef.current = null;
    }
  };

  if (loadError) {
    return (
      <div className={`flex h-full items-center justify-center p-6 ${className}`}>
        <Description>Não foi possível carregar a planta do condomínio.</Description>
      </div>
    );
  }

  if (!naturalSize) {
    return (
      <>
        <img
          src={imageUrl}
          alt=""
          className="hidden"
          onLoad={(event) => {
            const img = event.currentTarget;
            setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
          }}
          onError={() => setLoadError(true)}
        />
        <div className={`flex h-full flex-col items-center justify-center gap-2 ${className}`}>
          <Spinner />
          <Description>Carregando planta...</Description>
        </div>
      </>
    );
  }

  const visibleRect = draft ?? rect;

  return (
    <div className={`relative flex h-full min-h-0 flex-col ${className}`}>
      {editable ? (
        <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-2">
          <Description>
            {zoom > 1
              ? 'Arraste para desenhar a vaga · pinça ou botões para zoom'
              : 'Toque ou arraste na planta para marcar a vaga'}
          </Description>
          {rect ? (
            <Button size="sm" variant="danger" onPress={() => onChange?.(null)}>
              <Trash2 size={14} />
              Remover
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 overflow-auto bg-surface-secondary"
        style={{
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          justifyContent: display && display.width < boxSize.width ? 'center' : 'flex-start',
          alignItems: display && display.height < boxSize.height ? 'center' : 'flex-start',
        }}
        onWheel={handleWheel}
      >
        <div
          ref={imageWrapRef}
          className="relative shrink-0"
          style={{
            width: display?.width ?? '100%',
            height: display?.height,
            aspectRatio: `${naturalSize.width} / ${naturalSize.height}`,
            touchAction: editable ? 'none' : 'pan-x pan-y',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishDrawing}
          onPointerCancel={() => {
            drawingRef.current = null;
            setDraft(null);
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={imageUrl}
            alt="Planta do condomínio"
            draggable={false}
            className="pointer-events-none block h-full w-full select-none"
          />
          {visibleRect ? <RectOverlay rect={visibleRect} dashed={Boolean(draft)} /> : null}
        </div>
      </div>

      <div className="pointer-events-auto absolute bottom-3 right-3 z-10 flex items-center gap-1 rounded-lg border border-separator bg-overlay/95 p-1 shadow-md">
        {rect ? (
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label="Centralizar vaga"
            onPress={() => focusRect(rect)}
          >
            <Crosshair size={16} />
          </Button>
        ) : null}
        {zoom !== 1 ? (
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            aria-label="Resetar zoom"
            onPress={() => changeZoom(1)}
          >
            <RotateCcw size={14} />
          </Button>
        ) : null}
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label="Reduzir zoom"
          isDisabled={zoom <= MIN_FLOOR_PLAN_ZOOM}
          onPress={() => changeZoom(zoom - FLOOR_PLAN_ZOOM_STEP)}
        >
          <ZoomOut size={16} />
        </Button>
        <span className="min-w-12 px-1 text-center text-xs font-medium tabular-nums">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          aria-label="Aumentar zoom"
          isDisabled={zoom >= MAX_FLOOR_PLAN_ZOOM}
          onPress={() => changeZoom(zoom + FLOOR_PLAN_ZOOM_STEP)}
        >
          <ZoomIn size={16} />
        </Button>
      </div>
    </div>
  );
}
